import React from 'react';
import { Modal, Descriptions, Card, Button, Space, Divider } from 'antd';
import { SendOutlined, FileDoneOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { prescriptionService } from '../services';
import { formatCurrency, formatDate } from '../utils/format';
import './PrescriptionDetailModal.css';

interface PrescriptionDetailModalProps {
  visible: boolean;
  code: string | null;
  onClose: () => void;
}

const PrescriptionDetailModal: React.FC<PrescriptionDetailModalProps> = ({
  visible,
  code,
  onClose,
}) => {
  const queryClient = useQueryClient();

  const { data: detail, isLoading } = useQuery({
    queryKey: ['prescription-detail', code],
    queryFn: () => {
      if (!code) throw new Error('No code provided');
      return prescriptionService.getDetail(code);
    },
    enabled: !!code && visible,
  });

  const sendMutation = useMutation({
    mutationFn: (ids: string[]) => prescriptionService.sendToNational(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      queryClient.invalidateQueries({ queryKey: ['prescription-detail', code] });
    },
  });

  const exportMutation = useMutation({
    mutationFn: (ids: string[]) => prescriptionService.exportInvoice(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prescriptions'] });
      queryClient.invalidateQueries({ queryKey: ['prescription-detail', code] });
    },
  });

  if (!detail || isLoading) return null;

  return (
    <Modal
      title="Thông tin chi tiết đơn thuốc"
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1000}
      className="prescription-detail-modal"
    >
      <div className="modal-content">
        <div className="modal-left">
          <Card title="Thông tin khách hàng" className="info-card">
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Họ tên">
                {detail.patientInfo.fullName}
              </Descriptions.Item>
              <Descriptions.Item label="Địa chỉ">
                {detail.patientInfo.address}
              </Descriptions.Item>
              <Descriptions.Item label="CCCD / MST">
                {detail.patientInfo.idCard}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card title="Dịch vụ" className="info-card" style={{ marginTop: 16 }}>
            <div className="services-list">
              {detail.services.map((service, index) => (
                <div key={index} className="service-row">
                  <span className="service-name">{service.name}:</span>
                  <span className="service-amount">{formatCurrency(service.amount)}</span>
                </div>
              ))}
              <Divider />
              <div className="service-row">
                <span className="service-name">Hình thức thanh toán:</span>
                <span>{detail.paymentMethod}</span>
              </div>
            </div>
          </Card>
        </div>

        <div className="modal-right">
          <Card title="Toa thuốc" className="prescription-card">
            <div className="medicines-list">
              {detail.medicines.map((medicine, index) => (
                <div key={index} className="medicine-item">
                  <div className="medicine-header">
                    <span className="medicine-name">{medicine.name}</span>
                    <span className="medicine-quantity">{medicine.quantity}</span>
                  </div>
                  <div className="medicine-instructions">{medicine.instructions}</div>
                  {index < detail.medicines.length - 1 && <Divider />}
                </div>
              ))}
            </div>
          </Card>

          <div className="action-buttons-right">
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => exportMutation.mutate([detail.id])}
              loading={exportMutation.isPending}
              block
            >
              Xuất hóa đơn khám & Thuốc
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => exportMutation.mutate([detail.id])}
              loading={exportMutation.isPending}
              block
            >
              Xuất hóa đơn công khám
            </Button>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={() => exportMutation.mutate([detail.id])}
              loading={exportMutation.isPending}
              block
            >
              Xuất hóa đơn bán thuốc
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PrescriptionDetailModal;

